import type {
  Contact,
  Account,
  ConversationEntry,
  Customer,
  CustomerSummary,
  DashboardStats,
  FansPoint,
  LicenseRecord,
  PromptSummary,
  ServerMeta,
  WorkspaceSnapshot,
} from "./types";

const BASE = "/api";
const TOKEN_KEY = "avm_admin_token";

export function getAdminToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setAdminToken(token: string) {
  sessionStorage.setItem(TOKEN_KEY, token.trim());
}

export function clearAdminToken() {
  sessionStorage.removeItem(TOKEN_KEY);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAdminToken();
  const resp = await fetch(BASE + path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers || {}),
    },
  });
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const data = await resp.json();
      if (data?.detail) detail = String(data.detail);
    } catch {
      // ignore
    }
    const err = new Error(detail) as Error & { status?: number };
    err.status = resp.status;
    throw err;
  }
  if (resp.status === 204) return undefined as unknown as T;
  return (await resp.json()) as T;
}

export const api = {
  meta: () => request<ServerMeta>("/meta"),
  stats: () => request<DashboardStats>("/stats"),

  listCustomers: () => request<{ customers: CustomerSummary[]; rev: number }>("/customers"),
  getCustomer: (id: string) =>
    request<{ customer: Customer; workspace: WorkspaceSnapshot }>(`/customers/${encodeURIComponent(id)}`),
  createCustomer: (body: Partial<Customer> & { contacts?: Contact[] }) =>
    request<Customer>("/customers", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateCustomer: (id: string, body: Partial<Customer>) =>
    request<Customer>(`/customers/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteCustomer: (id: string) =>
    request<{ deleted: string }>(`/customers/${encodeURIComponent(id)}`, { method: "DELETE" }),

  addContact: (id: string, c: Contact) =>
    request<Contact>(`/customers/${encodeURIComponent(id)}/contacts`, {
      method: "POST",
      body: JSON.stringify(c),
    }),
  removeContact: (id: string, kind: string, value: string) =>
    request<{ removed: Contact }>(
      `/customers/${encodeURIComponent(id)}/contacts?kind=${encodeURIComponent(kind)}&value=${encodeURIComponent(value)}`,
      { method: "DELETE" },
    ),

  addAccount: (id: string, body: Partial<Account>) =>
    request<Account>(`/customers/${encodeURIComponent(id)}/accounts`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateAccount: (id: string, aid: string, body: Partial<Account>) =>
    request<Account>(
      `/customers/${encodeURIComponent(id)}/accounts/${encodeURIComponent(aid)}`,
      {
        method: "PATCH",
        body: JSON.stringify(body),
      },
    ),
  removeAccount: (id: string, aid: string) =>
    request<{ removed: string }>(
      `/customers/${encodeURIComponent(id)}/accounts/${encodeURIComponent(aid)}`,
      { method: "DELETE" },
    ),
  addFansPoint: (id: string, aid: string, body: FansPoint) =>
    request<FansPoint>(
      `/customers/${encodeURIComponent(id)}/accounts/${encodeURIComponent(aid)}/fans`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  sign: (id: string, body: Record<string, unknown>) =>
    request<{ license: LicenseRecord; file_abs: string }>(
      `/customers/${encodeURIComponent(id)}/sign`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  downloadLicenseUrl: (id: string, filename: string) =>
    `${BASE}/customers/${encodeURIComponent(id)}/licenses/${encodeURIComponent(filename)}`,

  downloadLicense: async (id: string, filename: string) => {
    const token = getAdminToken();
    const resp = await fetch(api.downloadLicenseUrl(id, filename), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) {
      throw new Error(`下载失败：HTTP ${resp.status}`);
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  listConversations: (id: string) =>
    request<{ items: ConversationEntry[] }>(`/customers/${encodeURIComponent(id)}/conversations`),
  addConversation: (id: string, body: Partial<ConversationEntry>) =>
    request<ConversationEntry>(`/customers/${encodeURIComponent(id)}/conversations`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  removeConversation: (id: string, entryId: string) =>
    request<{ removed: ConversationEntry }>(
      `/customers/${encodeURIComponent(id)}/conversations/${encodeURIComponent(entryId)}`,
      { method: "DELETE" },
    ),

  listPrompts: (id: string) =>
    request<{ items: PromptSummary[] }>(`/customers/${encodeURIComponent(id)}/prompts`),
  readPrompt: (id: string, name: string) =>
    request<{ name: string; content: string }>(
      `/customers/${encodeURIComponent(id)}/prompts/${encodeURIComponent(name)}`,
    ),
  createPrompt: (id: string, body: { title?: string; name?: string; content: string }) =>
    request<PromptSummary>(`/customers/${encodeURIComponent(id)}/prompts`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updatePrompt: (id: string, name: string, content: string) =>
    request<PromptSummary>(
      `/customers/${encodeURIComponent(id)}/prompts/${encodeURIComponent(name)}`,
      { method: "PUT", body: JSON.stringify({ content }) },
    ),
  deletePrompt: (id: string, name: string) =>
    request<{ removed: string }>(
      `/customers/${encodeURIComponent(id)}/prompts/${encodeURIComponent(name)}`,
      { method: "DELETE" },
    ),
};
